import React, { createContext, useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router";
import { useNavigationContext } from "../../contexts/navigationContext";
import { useMobileScale } from "./mobileScale";

type Props = { children: React.ReactNode };

export type OfficeContextType = {
  itemIds: string[];            // REST v2 ids for your data layer (single or multi)
  selectionMode: "single" | "multi";
  mailboxReady: boolean;        // safe to call displayMessageForm, etc.
  hostName: string;
  isMobile: boolean;
};

export const OfficeContext = createContext<null | OfficeContextType>(null);

// Utility: safely convert id to REST v2 (for your queries)
const toRestV2 = (id: string) => {
  try {
    return Office.context.mailbox.convertToRestId(
      id,
      Office.MailboxEnums.RestVersion.v2_0
    );
  } catch {
    return id;
  }
};

const OfficeContainer: React.FC<Props> = (props) => {
  const navigate = useNavigate();
  const { setFromPage } = useNavigationContext();

  const [itemIds, setItemIds] = useState<string[]>([]);
  const [selectionMode, setSelectionMode] = useState<"single" | "multi">("single");
  const [mailboxReady, setMailboxReady] = useState(true);

  // A simple "generation" token to prevent out-of-order async from winning.
  const genRef = useRef(0);

  const hostName = Office.context.mailbox.diagnostics.hostName;
  const isMobile = hostName === "OutlookIOS" || hostName === "OutlookAndroid";

  const getSelectedItems = (): Promise<string[]> =>
    new Promise((resolve, reject) => {
      Office.context.mailbox.getSelectedItemsAsync((res) => {
        if (res.status === Office.AsyncResultStatus.Succeeded) {
          resolve(res.value.map((m) => m.itemId));
        } else {
          reject(res.error?.message);
        }
      });
    });

  const setIdsFromCurrentItem = useCallback(() => {
    // Reading-pane single-item context
    const current = Office?.context?.mailbox?.item?.itemId || null;
    if (!current) {
      setItemIds([]);
      return;
    }
    switch (hostName) {
      case "OutlookIOS":
      case "OutlookAndroid":
        setItemIds([current]);
        break;
      default:
        setItemIds([toRestV2(current)]);
    }
  }, [hostName]);

  // Warm-up helper to flip mailboxReady true after Outlook responds to any benign async
  const warmUpMailbox = useCallback(() => {
    setMailboxReady(false);
    try {
      Office.context.mailbox.item?.subject?.getAsync?.(() => {
        setMailboxReady(true);
      });
    } catch {
      setTimeout(() => setMailboxReady(true), 150);
    }
  }, []);

  const handleItemChanged = useCallback(() => {
    // Pinned-pane single item changed. Update single id and warm-up.
    setFromPage(false);
    setSelectionMode("single");
    setIdsFromCurrentItem();
    warmUpMailbox();
  }, [setFromPage, setIdsFromCurrentItem, warmUpMailbox]);

  const handleSelectedItemsChanged = useCallback(async () => {
    // Multi-select changed in the list. Refresh the selected ids.
    const myGen = ++genRef.current;
    try {
      const ids = await getSelectedItems();
      if (genRef.current !== myGen) return; // drop stale result

      if (ids.length <= 1) {
        // Back to single-selection. Use mailbox.item for authoritative context.
        setSelectionMode("single");
        setIdsFromCurrentItem();
      } else {
        setSelectionMode("multi");
        // Convert all to REST v2 for your data queries
        const restIds = ids.map(toRestV2);
        setItemIds(restIds);
      }
      warmUpMailbox();
    } catch (err) {
      console.error("getSelectedItemsAsync failed", err);
    }
  }, [setIdsFromCurrentItem, warmUpMailbox]);

  // NEW: Initialize with current selection state (single OR multi)
  const initializeSelection = useCallback(async () => {
    const myGen = ++genRef.current;
    
    // Check if getSelectedItemsAsync is available
    if (typeof Office.context.mailbox.getSelectedItemsAsync === "function") {
      try {
        const ids = await getSelectedItems();
        if (genRef.current !== myGen) return;

        if (ids.length > 1) {
          // Multi-selection on load
          setSelectionMode("multi");
          const restIds = ids.map(toRestV2);
          setItemIds(restIds);
          warmUpMailbox();
          return;
        }
      } catch (err) {
        console.error("Initial getSelectedItemsAsync failed", err);
      }
    }
    
    // Fall back to single item
    setSelectionMode("single");
    setIdsFromCurrentItem();
  }, [setIdsFromCurrentItem, warmUpMailbox]);

  const fetchInitializationContext = useCallback(() => {
    const item = Office.context.mailbox.item;
    if (item && typeof item.getInitializationContextAsync === "function") {
      item.getInitializationContextAsync((asyncResult) => {
        if (asyncResult.status === Office.AsyncResultStatus.Succeeded &&
            asyncResult.value?.length > 0) {
          try {
            const ctx = JSON.parse(asyncResult.value);
            if (ctx.route) navigate(ctx.route);
          } catch (e) {
            console.error("Failed to parse initialization context:", e);
          }
        } else if (asyncResult.status === Office.AsyncResultStatus.Failed) {
          console.error("getInitializationContextAsync error:", asyncResult.error?.message);
        }
      });
    }
  }, [navigate]);

  useMobileScale(isMobile);

  useEffect(() => {
    // CHANGED: Use initializeSelection instead of setIdsFromCurrentItem
    initializeSelection();
    fetchInitializationContext();

    // **Pinned-pane single item navigation**
    Office.context.mailbox.addHandlerAsync(
      Office.EventType.ItemChanged,
      handleItemChanged
    );

    // **Multi-select list changes**
    Office.context.mailbox.addHandlerAsync(
      Office.EventType.SelectedItemsChanged,
      handleSelectedItemsChanged
    );
  }, [fetchInitializationContext, handleItemChanged, handleSelectedItemsChanged, initializeSelection]);

  if (itemIds.length === 0) return null;

  return (
    <OfficeContext.Provider value={{ itemIds, selectionMode, mailboxReady, hostName, isMobile }}>
      {props.children}
    </OfficeContext.Provider>
  );
};

export default OfficeContainer;